import type { Observable } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import type { AttributeValue } from '@opentelemetry/api';

/**
 * A generic, global store for holding OpenTelemetry attributes that need to be
 * shared between different parts of the application, especially between the
 * pre-Angular tracing world and the Angular DI world.
 */
class AttributeStore {
  private readonly store = new BehaviorSubject<AttributeMap>(new Map());

  /**
   * Sets or updates a single attribute in the store.
   * This is the primary method for producers (like Angular services) to use.
   * @param key The semantic attribute key (e.g., 'discovery.document.status').
   * @param value The value of the attribute, which must be a valid OTel AttributeValue.
   */
  public setAttribute(key: string, value: AttributeValue): void {
    const currentMap: AttributeMap = this.store.getValue();
    const newMap = new Map(currentMap);
    newMap.set(key, value);
    this.store.next(newMap);
  }

  /**
   * Returns an observable of the entire attribute map.
   * This is what consumers (like the SpanProcessor) will subscribe to.
   */
  public getAttributes$(): Observable<AttributeMap> {
    return this.store.asObservable();
  }
}

export type AttributeMap = Map<string, AttributeValue>;

export const attributeStore = new AttributeStore();
